import React, { useState } from 'react';
import { Box, Text, Link } from '@primer/react';
import DSAGuide from './DSAGuide';
import DSAFlashcard from './DSAFlashcard';

const LearnDSA = () => {
    const [activeSection, setActiveSection] = useState(null);

    const toggleSection = (section) => {
        setActiveSection(activeSection === section ? null : section);
    };

    return (
        <Box mb={6}>
            <style>{`
                .learn-dsa-container {
                    max-width: 1200px;
                    margin: 0 auto;
                }
                
                .intro-section {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                }
                
                .intro-title {
                    font-size: 1.5rem;
                    font-weight: 600;
                    margin-bottom: 0.75rem;
                    text-align: center;
                    color: #1a202c;
                }
                
                .intro-text {
                    font-size: 0.95rem;
                    line-height: 1.5;
                    margin-bottom: 1rem;
                    text-align: center;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                    color: #4a5568;
                }
                
                .learn-dsa-link {
                    display: block;
                    margin: 0 auto;
                    max-width: 300px;
                    transition: all 0.2s ease;
                    border-radius: 8px;
                    overflow: hidden;
                }
                
                .learn-dsa-link:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                }
                
                .learn-dsa-image {
                    width: 100%;
                    height: auto;
                    display: block;
                }
                
                .learn-dsa-container .collapsible-section {
                    background: white;
                    border-radius: 6px;
                    border: 1px solid #e2e8f0;
                    margin-bottom: 0.75rem;
                    overflow: hidden;
                }
                
                .learn-dsa-container .section-header {
                    background: #f8fafc !important;
                    color: #374151 !important;
                    padding: 0.75rem 1rem !important;
                    cursor: pointer;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    transition: background-color 0.2s ease !important;
                    user-select: none;
                    border-bottom: 1px solid #e2e8f0;
                    box-shadow: none !important;
                    border-radius: 0 !important;
                    margin-bottom: 0 !important;
                    transform: none !important;
                }
                
                .learn-dsa-container .section-header:hover {
                    background: #f1f5f9 !important;
                    transform: none !important;
                    box-shadow: none !important;
                }
                
                .learn-dsa-container .section-title {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    font-size: 0.9rem !important;
                    font-weight: 500 !important;
                    letter-spacing: normal !important;
                }
                
                .learn-dsa-container .section-icon {
                    font-size: 1rem !important;
                    filter: none !important;
                }
                
                .learn-dsa-container .toggle-icon {
                    font-size: 0.8rem !important;
                    transition: transform 0.2s ease !important;
                    transform: rotate(0deg) !important;
                    color: #6b7280 !important;
                    margin-left: 0 !important;
                }
                
                .learn-dsa-container .toggle-icon.active {
                    transform: rotate(180deg) !important;
                }
                
                .learn-dsa-container .section-content {
                    max-height: 0;
                    overflow: hidden;
                    transition: max-height 0.3s ease-out;
                }
                
                .learn-dsa-container .section-content.active {
                    max-height: none;
                    overflow: visible;
                }
                
                .learn-dsa-container .section-description {
                    padding: 0.75rem 1rem;
                    background: #fafbfc;
                    color: #6b7280;
                    line-height: 1.4;
                    font-size: 0.85rem;
                    border-bottom: 1px solid #e2e8f0;
                }
                
                .component-wrapper {
                    padding: 0;
                }
                
                .original-tools-explanation {
                    text-align: center;
                    color: #6b7280;
                    font-size: 0.85rem;
                    line-height: 1.4;
                    margin-bottom: 1.5rem;
                    font-style: italic;
                }
                
                @media (max-width: 768px) {
                    .intro-section {
                        padding: 1rem;
                        margin-bottom: 1.5rem;
                    }
                    
                    .intro-title {
                        font-size: 1.25rem;
                    }
                    
                    .intro-text {
                        font-size: 0.9rem;
                    }
                    
                    .learn-dsa-container .section-header {
                        padding: 0.75rem !important;
                    }
                    
                    .learn-dsa-container .section-title {
                        font-size: 0.85rem !important;
                    }
                    
                    .learn-dsa-container .section-description {
                        padding: 0.75rem;
                        font-size: 0.8rem;
                    }
                    
                    .learn-dsa-link {
                        max-width: 250px;
                    }
                    
                    .original-tools-explanation {
                        font-size: 0.8rem;
                        margin-bottom: 1rem;
                    }
                }
            `}</style>
            
            <div className="learn-dsa-container">
                {/* Introduction Section */}
                <div className="intro-section">
                    <h1 className="intro-title">Learn DSA</h1>
                    <p className="intro-text">
                        Based on the feedback I've received for the Cheatsheet and Flashcards, I've decided to develop a free-to-use service to help people work on their DSA skills. Check out the new dedicated platform:
                    </p>
                    <Link 
                        href="https://learn-dsa.dev" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="learn-dsa-link"
                    >
                        <img 
                            src="/learn-dsa.png" 
                            alt="Visit learn-dsa.dev" 
                            className="learn-dsa-image"
                        />
                    </Link>
                </div>

                {/* Original DSA Tools - Collapsible Sections */}
                <Box>
                    <Text 
                        as="h2" 
                        fontSize={3} 
                        fontWeight="semibold" 
                        color="fg.default" 
                        mb={2}
                        textAlign="center"
                    >
                        Original DSA Tools
                    </Text>
                    
                    <div className="original-tools-explanation">
                        These are the original tools I built before creating the dedicated platform. 
                        They're still available here for reference and continued use.
                    </div>
                    
                    {/* DSA Cheatsheet Section */}
                    <div className="collapsible-section">
                        <div 
                            className="section-header"
                            onClick={() => toggleSection('cheatsheet')}
                        >
                            <div className="section-title">
                                <span className="section-icon">📚</span>
                                <span>DSA Cheatsheet</span>
                            </div>
                            <span className={`toggle-icon ${activeSection === 'cheatsheet' ? 'active' : ''}`}>
                                ▼
                            </span>
                        </div>
                        
                        <div className="section-description">
                            Comprehensive reference guide with detailed explanations, complexity analysis, and code examples.
                        </div>
                        
                        <div className={`section-content ${activeSection === 'cheatsheet' ? 'active' : ''}`}>
                            <div className="component-wrapper">
                                {activeSection === 'cheatsheet' && <DSAGuide />}
                            </div>
                        </div>
                    </div>

                    {/* DSA Flashcards Section */}
                    <div className="collapsible-section">
                        <div 
                            className="section-header"
                            onClick={() => toggleSection('flashcards')}
                        >
                            <div className="section-title">
                                <span className="section-icon">⚡</span>
                                <span>DSA Flashcards</span>
                            </div>
                            <span className={`toggle-icon ${activeSection === 'flashcards' ? 'active' : ''}`}>
                                ▼
                            </span>
                        </div>
                        
                        <div className="section-description">
                            Interactive learning tool with code examples and progressive hints for algorithm identification.
                        </div>
                        
                        <div className={`section-content ${activeSection === 'flashcards' ? 'active' : ''}`}>
                            <div className="component-wrapper">
                                {activeSection === 'flashcards' && <DSAFlashcard />}
                            </div>
                        </div>
                    </div>
                </Box>
            </div>
        </Box>
    );
};

export default LearnDSA;
